/**
 * Atualiza título, meta description, endereço canônico e tags de
 * compartilhamento (Open Graph) enquanto a página do site está aberta, e
 * restaura os valores anteriores ao sair.
 */

import { useEffect } from 'react';

/** Endereço público do site, sem barra final */
export const SITE_URL = (import.meta.env.VITE_SITE_URL || 'https://vendeai.net.br').replace(
  /\/+$/,
  '',
);

type HeadTag = { selector: string; create: () => HTMLElement; attr: 'content' | 'href' };

function upsert({ selector, create, attr }: HeadTag, value: string): () => void {
  let el = document.head.querySelector<HTMLElement>(selector);
  // Tags vindas da pré-renderização (data-prerender) pertencem à página
  // gerada no build: são removidas ao sair dela, como as criadas aqui.
  const created = !el || el.hasAttribute('data-prerender');
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  const previous = el.getAttribute(attr);
  el.setAttribute(attr, value);
  const target = el;
  return () => {
    if (created) target.remove();
    else if (previous !== null) target.setAttribute(attr, previous);
  };
}

const meta = (key: 'name' | 'property', name: string): HeadTag => ({
  selector: `meta[${key}="${name}"]`,
  create: () => {
    const el = document.createElement('meta');
    el.setAttribute(key, name);
    return el;
  },
  attr: 'content',
});

const CANONICAL: HeadTag = {
  selector: 'link[rel="canonical"]',
  create: () => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    return el;
  },
  attr: 'href',
};

/**
 * @param title Título da aba e do compartilhamento
 * @param description Meta description
 * @param path Caminho da página (ex.: "/para/pizzaria") usado no canonical
 * @param ogType Tipo Open Graph ("article" nos artigos do blog)
 */
export function usePageMeta(
  title: string,
  description: string,
  path = '/',
  ogType: 'website' | 'article' = 'website',
) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;
    const restore = [
      upsert(meta('name', 'description'), description),
      upsert(CANONICAL, url),
      upsert(meta('property', 'og:type'), ogType),
      upsert(meta('property', 'og:site_name'), 'Vende Aí'),
      upsert(meta('property', 'og:locale'), 'pt_BR'),
      upsert(meta('property', 'og:title'), title),
      upsert(meta('property', 'og:description'), description),
      upsert(meta('property', 'og:url'), url),
      upsert(meta('property', 'og:image'), `${SITE_URL}/og-image.png`),
      upsert(meta('name', 'twitter:card'), 'summary_large_image'),
    ];
    return () => {
      document.title = previousTitle;
      restore.reverse().forEach(fn => fn());
      // Dados estruturados da página pré-renderizada não valem para outras rotas
      document.head
        .querySelectorAll('script[type="application/ld+json"][data-prerender]')
        .forEach(el => el.remove());
    };
  }, [title, description, path, ogType]);
}
